import { Input } from "antd";
import { TextAreaRef } from "antd/lib/input/TextArea";
import cn from "classnames";
import { FC, RefObject, useCallback, useEffect, useMemo, useRef } from "react";

import { KeyCode } from "@app/shared/lib/enums/KeyCode";
import { useLoc } from "@app/shared/lib/hooks";

export type EditorHandler = {
  focus: () => void;
  blur: () => void;
  insert: (text: string) => void;
};

type Props = {
  text: string;
  enterToSubmit: boolean;
  disabled: boolean;
  placeholder?: string;
  autoSize?: { minRows?: number; maxRows?: number };
  autoFocus?: boolean;
  handlerRef?: RefObject<EditorHandler | null>;
  className?: string;
  onSend: () => Promise<boolean>;
  onChange: (text: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement, Element>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement, Element>) => void;
};

export const Editor: FC<Props> = ({
  placeholder,
  text,
  enterToSubmit,
  autoSize,
  autoFocus,
  disabled,
  handlerRef,
  className,
  onChange,
  onSend,
  onFocus,
  onBlur,
}) => {
  const loc = useLoc();
  const inputRef = useRef<TextAreaRef>(null);
  const handler = useMemo<EditorHandler>(
    () => ({
      focus: () => {
        inputRef.current?.focus();
        // don't ask me why 1000 idk it works
        inputRef.current?.resizableTextArea?.textArea.setSelectionRange(
          1000,
          1000,
        );
      },
      blur: () => inputRef.current?.blur(),
      insert: (insertedText: string) => {
        if (!inputRef.current) return;
        inputRef.current.focus();
        const el = inputRef.current.resizableTextArea!.textArea;
        const [start, end] = [el.selectionStart, el.selectionEnd];
        el.setRangeText(insertedText, start, end, "end");
        onChange(el.value);
      },
    }),
    [onChange],
  );

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    if (handlerRef) handlerRef.current = handler;
    if (autoFocus === true) handler.focus();
  }, [autoFocus, handler, handlerRef]);

  const handleInputKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!enterToSubmit) return;
      if (e.keyCode !== KeyCode.Enter || e.shiftKey) return;

      e.preventDefault();
      await onSend();
      inputRef.current?.focus();
    },
    [enterToSubmit, onSend],
  );

  return (
    <Input.TextArea
      ref={inputRef}
      value={text}
      className={cn("msg-edit-input", className)}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder ?? loc.word("Enter comment")}
      autoSize={autoSize}
      onKeyDown={handleInputKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};
